import { SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import {
  PLANNING_ICON_CONTROL,
  PLANNING_ICON_CONTROL_ACTIVE,
  PLANNING_ICON_CONTROL_IDLE,
} from "./planningIconControl";

export interface PlanningExpandingSearchProps {
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly placeholder?: string;
  readonly className?: string;
}

/**
 * Linear trailing search: circular icon expands into a pill input.
 * Shared by Projects listing filter chrome and project-home task trailing.
 */
export function PlanningExpandingSearch(props: PlanningExpandingSearchProps) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasQuery = props.search.trim().length > 0;

  useEffect(() => {
    if (!expanded) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (containerRef.current?.contains(target)) return;
      setExpanded(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [expanded]);

  return (
    <div ref={containerRef} className={cn("relative flex h-7 items-center", props.className)}>
      <div
        className={cn(
          "overflow-hidden transition-[width,opacity] duration-200 ease-out",
          expanded ? "w-52 opacity-100" : "w-7 opacity-100",
        )}
      >
        {expanded ? (
          <label className="relative block h-7 w-full">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <input
              ref={inputRef}
              type="search"
              value={props.search}
              onChange={(event) => props.onSearchChange(event.currentTarget.value)}
              placeholder={props.placeholder ?? "Search…"}
              aria-label={props.ariaLabel}
              className={cn(
                "h-7 w-full rounded-full border border-border bg-muted/40 pr-3 pl-8 text-xs text-foreground",
                "placeholder:text-muted-foreground/50",
                "outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40",
              )}
            />
          </label>
        ) : (
          <button
            type="button"
            aria-label={props.ariaLabel}
            title="Search"
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            className={cn(
              PLANNING_ICON_CONTROL,
              "relative",
              hasQuery ? PLANNING_ICON_CONTROL_ACTIVE : PLANNING_ICON_CONTROL_IDLE,
            )}
          >
            <SearchIcon className="size-3.5" aria-hidden />
            {hasQuery ? (
              <span
                className="absolute top-1 right-1 size-1.5 rounded-full bg-hue-sky"
                aria-hidden
              />
            ) : null}
          </button>
        )}
      </div>
    </div>
  );
}
